import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import './StudentDashboard.css';

type TabType = 'library' | 'schools' | 'downloads';

interface ContentItem {
  id: string;
  school_id?: string;
  title: string;
  title_fa?: string;
  description?: string;
  description_fa?: string;
  r2_audio_path?: string;
  r2_slides_path?: string;
  r2_package_path?: string;
  sync_data?: string;
  duration_seconds?: number;
  slide_count?: number;
  status: string;
  created_at: string;
  school_name?: string;
  school_name_fa?: string;
}

interface SchoolItem {
  id: string;
  name: string;
  name_fa?: string;
  description?: string;
  description_fa?: string;
  category?: string;
  subscriber_count: number;
  content_count: number;
  is_subscribed: boolean;
}

interface SubscriptionItem {
  id: string;
  school_id: string;
  school_name: string;
  school_name_fa?: string;
  school_description?: string;
  school_description_fa?: string;
  school_category?: string;
  content_count: number;
  created_at: string;
}

export function StudentDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isLoading, accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('library');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isRTL = i18n.language === 'fa';

  // Handle redirects in useEffect
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
      } else if (user.role === 'teacher') {
        navigate('/teacher');
      }
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className={`student-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="sd-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  // Show loading while redirecting
  if (!user || user.role === 'teacher') {
    return (
      <div className={`student-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="sd-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className={`student-dashboard ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <header className="sd-header">
        <div className="sd-header-content">
          <a href="/" className="sd-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </a>
          <div className="sd-header-actions">
            <LanguageSwitcher />
            <button onClick={handleLogout} className="sd-logout-btn">
              {t('common.logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="sd-layout">
        {/* Sidebar Navigation */}
        <aside className="sd-sidebar">
          <div className="sd-user-info">
            <div className="sd-user-avatar">
              {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
            </div>
            <div className="sd-user-details">
              <span className="sd-user-name">{user.name || user.email}</span>
              <span className="sd-user-role">{t('auth.student')}</span>
            </div>
          </div>

          <nav className="sd-nav">
            <button
              className={`sd-nav-item ${activeTab === 'library' ? 'active' : ''}`}
              onClick={() => setActiveTab('library')}
            >
              <span className="sd-nav-icon">📚</span>
              <span className="sd-nav-label">{i18n.language === 'fa' ? 'کتابخانه من' : 'My Library'}</span>
            </button>
            <button
              className="sd-nav-item"
              onClick={() => navigate('/curriculum')}
            >
              <span className="sd-nav-icon">🎓</span>
              <span className="sd-nav-label">{i18n.language === 'fa' ? 'نصاب هوش مصنوعی' : 'AI Curriculum'}</span>
            </button>
            <button
              className={`sd-nav-item ${activeTab === 'schools' ? 'active' : ''}`}
              onClick={() => setActiveTab('schools')}
            >
              <span className="sd-nav-icon">🏫</span>
              <span className="sd-nav-label">{t('schools.title')}</span>
            </button>
            <button
              className={`sd-nav-item ${activeTab === 'downloads' ? 'active' : ''}`}
              onClick={() => setActiveTab('downloads')}
            >
              <span className="sd-nav-icon">📥</span>
              <span className="sd-nav-label">{t('content.download')}</span>
            </button>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="sd-main">
          {activeTab === 'library' && <LibraryTab accessToken={accessToken} />}
          {activeTab === 'schools' && <SchoolsTab accessToken={accessToken} />}
          {activeTab === 'downloads' && <DownloadsTab accessToken={accessToken} />}
        </main>
      </div>
    </div>
  );
}

// Library Tab - Content from subscribed schools
function LibraryTab({ accessToken }: { accessToken: string | null }) {
  const { i18n } = useTranslation();
  const [content, setContent] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchContent = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const url = new URL('/api/student/content', window.location.origin);
      if (searchQuery) {
        url.searchParams.set('search', searchQuery);
      }

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch content');
      }

      setContent(data.data?.content || []);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch content');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchContent();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchContent]);

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading && content.length === 0) {
    return (
      <div className="sd-tab-content">
        <div className="sd-loading">{i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="sd-tab-content">
      <div className="sd-tab-header">
        <h2>{i18n.language === 'fa' ? 'کتابخانه من' : 'My Library'}</h2>
        <div className="sd-search-box">
          <input
            type="text"
            placeholder={i18n.language === 'fa' ? 'جستجوی محتوا...' : 'Search content...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="sd-search-input"
          />
          <span className="sd-search-icon">🔍</span>
        </div>
      </div>

      {error && (
        <div className="sd-error-message">{error}</div>
      )}

      {content.length === 0 ? (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">📚</div>
          <h3>{i18n.language === 'fa' ? 'هنوز محتوایی ندارید' : 'No content yet'}</h3>
          <p>
            {i18n.language === 'fa'
              ? 'برای دسترسی به محتوا، ابتدا در یک مدرسه عضو شوید.'
              : 'Subscribe to a school to access content.'}
          </p>
        </div>
      ) : (
        <div className="sd-content-grid">
          {content.map(item => (
            <ContentCard 
              key={item.id} 
              content={item} 
              formatDuration={formatDuration}
              accessToken={accessToken}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Content Card Component
function ContentCard({ 
  content, 
  formatDuration,
  accessToken,
  onDownloadStart
}: { 
  content: ContentItem;
  formatDuration: (seconds?: number) => string;
  accessToken?: string | null;
  onDownloadStart?: (content: ContentItem) => void;
}) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  
  const displayTitle = i18n.language === 'fa' && content.title_fa ? content.title_fa : content.title;
  const displayDescription = i18n.language === 'fa' && content.description_fa 
    ? content.description_fa 
    : content.description;
  const schoolName = i18n.language === 'fa' && content.school_name_fa 
    ? content.school_name_fa 
    : content.school_name;

  const handlePlay = () => {
    navigate(`/player/${content.id}`);
  };

  const handleDownload = async () => {
    if (!accessToken || isDownloading) return;
    
    setIsDownloading(true);
    
    try {
      const response = await fetch(`/api/student/content/${content.id}/download`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Download failed');
      }

      if (data.data?.downloadUrl) {
        // Direct download from R2
        window.open(data.data.downloadUrl, '_blank');
      } else if (data.data?.requiresClientGeneration) {
        // Notify parent to handle client-side generation
        if (onDownloadStart) {
          onDownloadStart(content);
        }
      }
    } catch (err) {
      console.error('Download error:', err);
      alert(i18n.language === 'fa' ? 'خطا در دانلود' : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="sd-content-card">
      <div className="sd-content-thumbnail">
        <span className="sd-content-icon">🎓</span>
        {content.duration_seconds && (
          <span className="sd-content-duration">{formatDuration(content.duration_seconds)}</span>
        )}
      </div>
      <div className="sd-content-info">
        <h3 className="sd-content-title">{displayTitle}</h3>
        {schoolName && (
          <span className="sd-content-school">{schoolName}</span>
        )}
        {displayDescription && (
          <p className="sd-content-description">{displayDescription}</p>
        )}
        <div className="sd-content-meta">
          {content.slide_count && (
            <span className="sd-meta-item">
              📄 {content.slide_count} {i18n.language === 'fa' ? 'اسلاید' : 'slides'}
            </span>
          )}
        </div>
        <div className="sd-content-actions">
          <button className="sd-action-btn primary" onClick={handlePlay}>
            ▶️ {i18n.language === 'fa' ? 'پخش' : 'Play'}
          </button>
          <button 
            className="sd-action-btn secondary" 
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading 
              ? (i18n.language === 'fa' ? '⏳' : '⏳')
              : '📥'
            } {i18n.language === 'fa' ? 'دانلود' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Schools Tab - Discover and subscribe to schools
function SchoolsTab({ accessToken }: { accessToken: string | null }) {
  const { t, i18n } = useTranslation();
  const [schools, setSchools] = useState<SchoolItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [viewMode, setViewMode] = useState<'discover' | 'subscribed'>('subscribed');

  const categories = [
    { value: '', label: i18n.language === 'fa' ? 'همه' : 'All' },
    { value: 'math', label: t('schools.categories.math') },
    { value: 'science', label: t('schools.categories.science') },
    { value: 'language', label: t('schools.categories.language') },
    { value: 'history', label: t('schools.categories.history') },
    { value: 'arts', label: t('schools.categories.arts') },
    { value: 'technology', label: t('schools.categories.technology') },
    { value: 'other', label: t('schools.categories.other') }
  ];

  const fetchSchools = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const url = new URL('/api/discover/schools', window.location.origin);
      if (searchQuery) url.searchParams.set('search', searchQuery);
      if (selectedCategory) url.searchParams.set('category', selectedCategory);

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch schools');
      }

      setSchools(data.data?.schools || []);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch schools');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, searchQuery, selectedCategory]);

  const fetchSubscriptions = useCallback(async () => {
    if (!accessToken) return;
    
    try {
      const response = await fetch('/api/subscriptions', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSubscriptions(data.data?.subscriptions || []);
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    }
  }, [accessToken]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (viewMode === 'discover') {
        fetchSchools();
      } else {
        fetchSubscriptions();
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchSchools, fetchSubscriptions, viewMode]);

  const handleSubscribe = async (schoolId: string) => {
    if (!accessToken) return;

    try {
      const response = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ school_id: schoolId })
      });

      if (response.ok) {
        // Update local state
        setSchools(prev => prev.map(s => 
          s.id === schoolId ? { ...s, is_subscribed: true, subscriber_count: s.subscriber_count + 1 } : s
        ));
        fetchSubscriptions();
      }
    } catch (err) {
      console.error('Error subscribing:', err);
    }
  };

  const handleUnsubscribe = async (subscriptionId: string, schoolId: string) => {
    if (!accessToken) return;

    try {
      const response = await fetch(`/api/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSubscriptions(prev => prev.filter(s => s.id !== subscriptionId));
        setSchools(prev => prev.map(s => 
          s.id === schoolId ? { ...s, is_subscribed: false, subscriber_count: Math.max(0, s.subscriber_count - 1) } : s
        ));
      }
    } catch (err) {
      console.error('Error unsubscribing:', err);
    }
  };

  if (isLoading && schools.length === 0 && subscriptions.length === 0) {
    return (
      <div className="sd-tab-content">
        <div className="sd-loading">{i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="sd-tab-content">
      <div className="sd-tab-header">
        <h2>{t('schools.title')}</h2>
        <div className="sd-view-toggle">
          <button 
            className={`sd-toggle-btn ${viewMode === 'subscribed' ? 'active' : ''}`}
            onClick={() => setViewMode('subscribed')}
          >
            {i18n.language === 'fa' ? 'اشتراک‌های من' : 'My Subscriptions'}
          </button>
          <button 
            className={`sd-toggle-btn ${viewMode === 'discover' ? 'active' : ''}`}
            onClick={() => setViewMode('discover')}
          >
            {i18n.language === 'fa' ? 'کشف مدارس' : 'Discover'}
          </button>
        </div>
      </div>

      {viewMode === 'discover' && (
        <div className="sd-filters">
          <div className="sd-search-box">
            <input
              type="text"
              placeholder={i18n.language === 'fa' ? 'جستجوی مدرسه...' : 'Search schools...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="sd-search-input"
            />
            <span className="sd-search-icon">🔍</span>
          </div>
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="sd-category-select"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="sd-error-message">{error}</div>
      )}

      {viewMode === 'subscribed' ? (
        subscriptions.length === 0 ? (
          <div className="sd-empty-state">
            <div className="sd-empty-icon">🏫</div>
            <h3>{i18n.language === 'fa' ? 'هنوز عضو مدرسه‌ای نیستید' : 'No subscriptions yet'}</h3>
            <p>
              {i18n.language === 'fa'
                ? 'مدارس را کشف کنید و عضو شوید تا به محتوای آن‌ها دسترسی داشته باشید.'
                : 'Discover schools and subscribe to access their content.'}
            </p>
            <button className="sd-action-btn primary" onClick={() => setViewMode('discover')}>
              {i18n.language === 'fa' ? 'کشف مدارس' : 'Discover Schools'}
            </button>
          </div>
        ) : (
          <div className="sd-schools-grid">
            {subscriptions.map(sub => (
              <SubscriptionCard 
                key={sub.id} 
                subscription={sub} 
                onUnsubscribe={() => handleUnsubscribe(sub.id, sub.school_id)}
              />
            ))}
          </div>
        )
      ) : (
        schools.length === 0 ? (
          <div className="sd-empty-state">
            <div className="sd-empty-icon">🔍</div>
            <h3>{i18n.language === 'fa' ? 'مدرسه‌ای یافت نشد' : 'No schools found'}</h3>
            <p>
              {i18n.language === 'fa'
                ? 'جستجوی خود را تغییر دهید یا فیلترها را پاک کنید.'
                : 'Try a different search or clear filters.'}
            </p>
          </div>
        ) : (
          <div className="sd-schools-grid">
            {schools.map(school => (
              <SchoolCard 
                key={school.id} 
                school={school} 
                onSubscribe={() => handleSubscribe(school.id)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

// School Card Component
function SchoolCard({ 
  school, 
  onSubscribe 
}: { 
  school: SchoolItem;
  onSubscribe: () => void;
}) {
  const { t, i18n } = useTranslation();
  
  const displayName = i18n.language === 'fa' && school.name_fa ? school.name_fa : school.name;
  const displayDescription = i18n.language === 'fa' && school.description_fa 
    ? school.description_fa 
    : school.description;
  const categoryLabel = school.category ? t(`schools.categories.${school.category}`) : null;

  return (
    <div className="sd-school-card">
      <div className="sd-school-header">
        <h3>{displayName}</h3>
        {categoryLabel && (
          <span className="sd-school-category">{categoryLabel}</span>
        )}
      </div>
      
      {displayDescription && (
        <p className="sd-school-description">{displayDescription}</p>
      )}

      <div className="sd-school-stats">
        <div className="sd-stat">
          <span className="sd-stat-value">{school.subscriber_count}</span>
          <span className="sd-stat-label">{t('schools.subscribers')}</span>
        </div>
        <div className="sd-stat">
          <span className="sd-stat-value">{school.content_count}</span>
          <span className="sd-stat-label">{t('schools.contents')}</span>
        </div>
      </div>

      <div className="sd-school-actions">
        {school.is_subscribed ? (
          <span className="sd-subscribed-badge">
            ✓ {i18n.language === 'fa' ? 'عضو شده' : 'Subscribed'}
          </span>
        ) : (
          <button className="sd-action-btn primary" onClick={onSubscribe}>
            {t('schools.subscribe')}
          </button>
        )}
      </div>
    </div>
  );
}

// Subscription Card Component
function SubscriptionCard({ 
  subscription, 
  onUnsubscribe 
}: { 
  subscription: SubscriptionItem;
  onUnsubscribe: () => void;
}) {
  const { t, i18n } = useTranslation();
  
  const displayName = i18n.language === 'fa' && subscription.school_name_fa 
    ? subscription.school_name_fa 
    : subscription.school_name;
  const displayDescription = i18n.language === 'fa' && subscription.school_description_fa 
    ? subscription.school_description_fa 
    : subscription.school_description;
  const categoryLabel = subscription.school_category 
    ? t(`schools.categories.${subscription.school_category}`) 
    : null;

  return (
    <div className="sd-school-card subscribed">
      <div className="sd-school-header">
        <h3>{displayName}</h3>
        {categoryLabel && (
          <span className="sd-school-category">{categoryLabel}</span>
        )}
      </div>
      
      {displayDescription && (
        <p className="sd-school-description">{displayDescription}</p>
      )}

      <div className="sd-school-stats">
        <div className="sd-stat">
          <span className="sd-stat-value">{subscription.content_count}</span>
          <span className="sd-stat-label">{t('schools.contents')}</span>
        </div>
      </div>

      <div className="sd-school-actions">
        <button className="sd-action-btn danger" onClick={onUnsubscribe}>
          {t('schools.unsubscribe')}
        </button>
      </div>
    </div>
  );
}

// Downloads Tab - SD card package downloads
function DownloadsTab({ accessToken }: { accessToken: string | null }) {
  const { i18n } = useTranslation();
  const [content, setContent] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadProgress, setDownloadProgress] = useState<Record<string, number>>({});

  const fetchContent = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/student/content', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setContent(data.data?.content || []);
      }
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleDownload = async (item: ContentItem) => {
    if (!accessToken) return;
    
    setDownloadProgress(prev => ({ ...prev, [item.id]: 0 }));
    
    try {
      const response = await fetch(`/api/student/content/${item.id}/download`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Download failed');
      }

      // Simulate progress for UX
      for (let i = 10; i <= 90; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setDownloadProgress(prev => ({ ...prev, [item.id]: i }));
      }

      if (data.data?.downloadUrl) {
        // Direct download from R2
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.filename || `${item.title}.dnshch`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setDownloadProgress(prev => ({ ...prev, [item.id]: 100 }));
      
      // Clear progress after a delay
      setTimeout(() => {
        setDownloadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[item.id];
          return newProgress;
        });
      }, 2000);
    } catch (err) {
      console.error('Download error:', err);
      alert(i18n.language === 'fa' ? 'خطا در دانلود' : 'Download failed');
      setDownloadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[item.id];
        return newProgress;
      });
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="sd-tab-content">
        <div className="sd-loading">{i18n.language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div className="sd-tab-content">
      <div className="sd-tab-header">
        <h2>{i18n.language === 'fa' ? 'دانلود برای تبلت' : 'Download for Tablet'}</h2>
      </div>

      <div className="sd-download-info">
        <p>
          {i18n.language === 'fa'
            ? '📱 محتوا را به فرمت .dnshch دانلود کنید و روی کارت SD کپی کنید تا در تبلت دانشچه استفاده کنید.'
            : '📱 Download content as .dnshch files and copy to SD card for use on Danishcha tablets.'}
        </p>
      </div>

      {content.length === 0 ? (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">📥</div>
          <h3>{i18n.language === 'fa' ? 'محتوایی برای دانلود ندارید' : 'No content to download'}</h3>
          <p>
            {i18n.language === 'fa'
              ? 'ابتدا در یک مدرسه عضو شوید تا بتوانید محتوا دانلود کنید.'
              : 'Subscribe to a school first to download content.'}
          </p>
        </div>
      ) : (
        <div className="sd-download-list">
          {content.map(item => {
            const displayTitle = i18n.language === 'fa' && item.title_fa ? item.title_fa : item.title;
            const schoolName = i18n.language === 'fa' && item.school_name_fa 
              ? item.school_name_fa 
              : item.school_name;
            const progress = downloadProgress[item.id];
            const isDownloading = progress !== undefined && progress < 100;
            const isComplete = progress === 100;

            return (
              <div key={item.id} className="sd-download-item">
                <div className="sd-download-icon">🎓</div>
                <div className="sd-download-details">
                  <h4>{displayTitle}</h4>
                  {schoolName && <span className="sd-download-school">{schoolName}</span>}
                  <div className="sd-download-meta">
                    {item.slide_count && (
                      <span>📄 {item.slide_count} {i18n.language === 'fa' ? 'اسلاید' : 'slides'}</span>
                    )}
                    {item.duration_seconds && (
                      <span>⏱️ {formatDuration(item.duration_seconds)}</span>
                    )}
                  </div>
                </div>
                <div className="sd-download-action">
                  {isDownloading ? (
                    <div className="sd-download-progress">
                      <div className="sd-progress-bar">
                        <div 
                          className="sd-progress-fill" 
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span>{progress}%</span>
                    </div>
                  ) : isComplete ? (
                    <span className="sd-download-complete">✓</span>
                  ) : (
                    <button 
                      className="sd-action-btn primary"
                      onClick={() => handleDownload(item)}
                    >
                      📥 {i18n.language === 'fa' ? 'دانلود' : 'Download'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
